/**
 * Migration utility to update existing props with Wasabi URLs
 * This converts local image filenames to Wasabi cloud URLs
 */

import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = 'https://xwhnqxynhcvrciaucypv.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inh3aG5xeHluaGN2cmNpYXVjeXB2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQxNjg4NjcsImV4cCI6MjA2OTc0NDg2N30.GASvJDKDhvAz9IoW04jC5DsRYKBlQlLgRhLN4AOLrn4';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Wasabi configuration
const WASABI_BASE_URL = 'https://s3.us-central-1.wasabisys.com/propshare-network-1';

/**
 * Convert a local filename to a Wasabi URL
 * @param {string} filename - Local filename like "prop_123_main.jpg"
 * @returns {string} Wasabi URL
 */
const convertFilenameToWasabiUrl = (filename) => {
  // If it's already a URL, return as-is
  if (filename.startsWith('http')) {
    return filename;
  }
  
  // Convert local filename to Wasabi URL
  return `${WASABI_BASE_URL}/props/${filename}`;
};

/**
 * Migrate a single prop's images to Wasabi URLs
 * @param {Object} prop - Prop object from database
 * @returns {Promise<boolean>} Success status
 */
export const migratePropImages = async (prop) => {
  try {
    if (!prop.images || !Array.isArray(prop.images) || prop.images.length === 0) {
      console.log(`⏭️ Prop ${prop.name} has no images to migrate`);
      return true;
    }

    // Check if images are already Wasabi URLs
    const alreadyMigrated = prop.images.every(img => img.startsWith('http'));
    if (alreadyMigrated) {
      console.log(`✅ Prop ${prop.name} already has Wasabi URLs`);
      return true;
    }

    // Convert filenames to Wasabi URLs
    const wasabiUrls = prop.images.map(filename => convertFilenameToWasabiUrl(filename));
    
    console.log(`🔄 Migrating ${prop.name}:`);
    console.log(`   From: ${prop.images}`);
    console.log(`   To: ${wasabiUrls}`);

    // Update the prop in the database
    const { error } = await supabase
      .from('props')
      .update({ images: wasabiUrls })
      .eq('id', prop.id)
      .select();

    if (error) {
      console.error(`❌ Failed to migrate ${prop.name}:`, error);
      return false;
    }

    console.log(`✅ Successfully migrated ${prop.name}`);
    return true;

  } catch (error) {
    console.error(`❌ Error migrating prop ${prop.name}:`, error);
    return false;
  }
};

/**
 * Migrate all props to use Wasabi URLs
 * @returns {Promise<Object>} Migration results
 */
export const migrateAllPropsToWasabi = async () => {
  try {
    console.log('🚀 Starting migration of all props to Wasabi URLs...');
    
    // Fetch all props
    const { data: props, error } = await supabase
      .from('props')
      .select('id, name, images')
      .order('name');

    if (error) {
      throw new Error(`Failed to fetch props: ${error.message}`);
    }

    console.log(`📊 Found ${props.length} props to check`);

    let migrated = 0;
    let skipped = 0;
    let failed = 0;

    // Process each prop
    for (const prop of props) {
      const success = await migratePropImages(prop);
      
      if (success) {
        if (prop.images && prop.images.length > 0 && !prop.images[0].startsWith('http')) {
          migrated++;
        } else {
          skipped++;
        }
      } else {
        failed++;
      }

      // Add a small delay to avoid overwhelming the database
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const results = {
      total: props.length,
      migrated,
      skipped,
      failed
    };

    console.log('🎉 Migration complete!');
    console.log(`📊 Results:`, results);

    return results;

  } catch (error) {
    console.error('❌ Migration failed:', error);
    throw error;
  }
};

/**
 * Preview what the migration would do without actually changing anything
 * @returns {Promise<Array>} Array of migration actions
 */
export const previewMigration = async () => {
  try {
    console.log('👀 Previewing migration...');
    
    const { data: props, error } = await supabase
      .from('props')
      .select('id, name, images')
      .order('name');

    if (error) {
      throw new Error(`Failed to fetch props: ${error.message}`);
    }

    const preview = props.map(prop => {
      if (!prop.images || !Array.isArray(prop.images) || prop.images.length === 0) {
        return {
          id: prop.id,
          name: prop.name,
          action: 'skip',
          reason: 'No images',
          current: [],
          proposed: []
        };
      }

      const alreadyMigrated = prop.images.every(img => img.startsWith('http'));
      if (alreadyMigrated) {
        return {
          id: prop.id,
          name: prop.name,
          action: 'skip',
          reason: 'Already has Wasabi URLs',
          current: prop.images,
          proposed: prop.images
        };
      }

      const wasabiUrls = prop.images.map(filename => convertFilenameToWasabiUrl(filename));
      return {
        id: prop.id,
        name: prop.name,
        action: 'migrate',
        reason: 'Convert local filenames to Wasabi URLs',
        current: prop.images,
        proposed: wasabiUrls
      };
    });

    console.log('📋 Migration preview:', preview);
    return preview;

  } catch (error) {
    console.error('❌ Preview failed:', error);
    throw error;
  }
};

/**
 * Test if a Wasabi URL is accessible
 * @param {string} url - Wasabi URL to test
 * @returns {Promise<boolean>} Whether the URL is accessible
 */
export const testWasabiUrl = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error(`❌ Failed to test URL ${url}:`, error);
    return false;
  }
};

/**
 * Validate that migrated URLs are working
 * @param {number} sampleSize - Number of props to test (default: 5)
 * @returns {Promise<Object>} Validation results
 */
export const validateMigration = async (sampleSize = 5) => {
  try {
    console.log(`🧪 Validating migration with ${sampleSize} sample props...`);
    
    const { data: props, error } = await supabase
      .from('props')
      .select('id, name, images')
      .not('images', 'is', null)
      .limit(sampleSize);

    if (error) {
      throw new Error(`Failed to fetch props for validation: ${error.message}`);
    }

    let working = 0;
    let broken = 0;
    const results = [];

    for (const prop of props) {
      if (prop.images && prop.images.length > 0) {
        const firstImageUrl = prop.images[0];
        if (firstImageUrl.startsWith('http')) {
          const isAccessible = await testWasabiUrl(firstImageUrl);
          if (isAccessible) {
            working++;
            console.log(`✅ ${prop.name}: ${firstImageUrl} - Working`);
          } else {
            broken++;
            console.log(`❌ ${prop.name}: ${firstImageUrl} - Broken`);
          }
          results.push({
            propName: prop.name,
            url: firstImageUrl,
            working: isAccessible
          });
        }
      }
    }

    const validationResults = {
      tested: props.length,
      working,
      broken,
      details: results
    };

    console.log('🏁 Validation complete:', validationResults);
    return validationResults;

  } catch (error) {
    console.error('❌ Validation failed:', error);
    throw error;
  }
};
